import React, { useState, useEffect } from 'react';
import { CalendarPicker } from './CalendarPicker';

// ── Disposition Modal ──────────────────────────────────────────────────────────
// `initial` = draft saved by App while the modal was closed (same call) — the
// agent's typed notes/selection survive a close→reopen, and the 120s wrap-up
// cap reap (2026-10-22): drafts are captured live via onDraftChange and
// restored here instead of being destroyed with the modal.
// `fiche` = { first_name, last_name, phone } — shown at the top so the agent
// knows WHO they are dispositioning when reopening a previous fiche after the
// wrap-up cap reaped it (the live screen has moved on to the next lead).
export default function DispositionModal({ dispositions, onSubmit, onPause, onClose, tz = 'America/Toronto', rdvBooked = false, initial = null, onDraftChange = null, fiche = null, allowApptTry = false }) {
  const [selected, setSelected]         = useState(initial?.selected || null);
  const [notes, setNotes]               = useState(initial?.notes || '');
  const [callbackAt, setCallbackAt]     = useState(initial?.callbackAt || '');
  const [submitting, setSubmitting]     = useState(false);
  const [pauseAfter, setPauseAfter]     = useState(false);
  const [pauseCodeId, setPauseCodeId]   = useState('');
  const [pauseCodes, setPauseCodes]     = useState([]);
  const [showPauseNow, setShowPauseNow] = useState(false);
  const [pauseNowCode, setPauseNowCode] = useState('');
  // Barrage APPT (Karim, 2026-10-16) : message affiché quand l'agent clique APPT
  // sans RDV réservé — remplace l'ancien window.confirm contournable d'un clic.
  const [apptBlocked, setApptBlocked] = useState(null);

  // Live draft sync (2026-10-22): App stores the latest {selected, notes,
  // callbackAt} so closing the modal — voluntarily or via the wrap-up-cap reap
  // — never loses what the agent already typed. Restored via `initial`.
  useEffect(() => {
    if (onDraftChange) onDraftChange({ selected, notes, callbackAt });
  }, [selected, notes, callbackAt, onDraftChange]);

  useEffect(() => {
    fetch('/api/admin/pause-codes', {
      headers: { Authorization: `Bearer ${localStorage.getItem('agent_token')}` }
    }).then(r => r.json()).then(d => {
      setPauseCodes(d || []);
      if (d?.length) { setPauseCodeId(d[0].id); setPauseNowCode(d[0].id); }
    }).catch(() => {});
  }, []);

  const APPT_BLOCK_MSG = 'Pour APPT, le RDV doit d\'abord être réservé dans le script avec les données obligatoires : date, heure, client (conseiller), type et statut DNCL confirmé. Complétez la réservation dans le script — ou choisissez un autre résultat (ex. RAPPEL) et notez les détails en commentaire.';

  const handleSubmit = async (disp) => {
    const d = disp || selected;
    if (!d) return;
    if (d.is_callback && !callbackAt) { setSelected(d); return; }
    setSubmitting(true);
    try {
      await onSubmit(d.id, notes, d.is_callback ? callbackAt : null, pauseAfter ? pauseCodeId : null);
    } catch (err) {
      // Barrage serveur (409 APPT_REQUIRES_RDV) ou autre refus : la modale
      // reste OUVERTE, rien n'est perdu — l'agent voit le message du serveur.
      setApptBlocked(err?.message || APPT_BLOCK_MSG);
    } finally { setSubmitting(false); }
  };

  // ONE CLICK = SUBMIT (Karim, 2026-10-12 minimal-click rule). Non-callback
  // dispositions submit immediately on the single click; only RAPPEL opens
  // the date/time picker first. Engine-only codes (CNC/AMD) never show here.
  const handleDispositionClick = (d) => {
    if (submitting) return;
    // ── Barrage APPT (Karim, 2026-10-16) ──────────────────────────────────────
    // Incident : 3 dispositions APPT, une seule ligne rendezvous en base. Un
    // RDV SANS réservation réelle (date, heure, client, type, DNCL) est
    // IMPOSSIBLE — blocage dur côté navigateur ET 409 côté serveur (le serveur
    // a toujours le dernier mot). L'agent sans toutes les infos choisit un
    // autre résultat (ex. RAPPEL) et note les détails en commentaire.
    // In prev-fiche mode (late disposition after the wrap-up cap) the client-side
    // rdvBooked flag is unreliable (reset by the next call:assigned) — let the
    // SERVER 409 decide: it checks the real rendezvous rows for this lead/call.
    if (d.is_appointment && !rdvBooked && !allowApptTry) {
      setSelected(null);
      setApptBlocked(APPT_BLOCK_MSG);
      return;
    }
    setApptBlocked(null); // tout autre clic efface le message
    if (d.is_callback) { setSelected(d); return; }
    setSelected(d);
    handleSubmit(d);
  };
  const HIDDEN_CODES = ['CNC', 'AMD', 'NODISPO'];
  // active=0 (admin "deactivated") must actually remove the button — the API
  // returns all rows, so filter here (Karim, 2026-10-14).
  const visibleDispositions = (dispositions || []).filter(d => d.active !== 0 && !HIDDEN_CODES.includes(d.code));

  const handlePauseNow = async () => {
    if (onPause) await onPause(pauseNowCode);
    onClose();
  };

  return (
    <div className="dispo-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="dispo-modal">

        <div className="dispo-modal-header">
          <span>{fiche ? 'Fiche précédente — disposition tardive' : 'Disposition de l\'appel'}</span>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text2)', cursor:'pointer', fontSize:18 }}>✕</button>
        </div>

        <div style={{ padding: 16 }}>
          {/* Who the agent is dispositioning — only shown when reopening a
              PREVIOUS fiche (after the 120s wrap-up cap), where the live screen
              no longer shows the lead. Karim permanent rule: full name + phone. */}
          {fiche && (
            <div style={{
              display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:6,
              padding: '8px 12px', marginBottom: 14, borderRadius: 10,
              background: 'rgba(239,68,68,0.08)', border: '2px solid rgba(239,68,68,0.35)'
            }}>
              <div>
                <div style={{ fontSize:9, fontWeight:800, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--red)', marginBottom:2 }}>
                  Appel déjà terminé (wrap-up expiré)
                </div>
                <div style={{ fontSize:14, fontWeight:800, color:'var(--text)' }}>
                  {fiche.first_name} {fiche.last_name}
                </div>
              </div>
              <div style={{ fontFamily:'var(--mono)', fontSize:14, fontWeight:700, color:'var(--text)' }}>
                {fiche.phone}
              </div>
            </div>
          )}
          {/* Notes — optional. Karim 2026-10-15: the comment zone was removed
              with the minimal-click pass; restored for ALL dispositions.
              Placed ABOVE the grid so the agent can type BEFORE clicking
              (one-click submit is unchanged — the click still submits
              immediately with whatever is in the box). */}
          <div style={{ marginBottom:14 }}>
            <div className="lbl" style={{ marginBottom:5 }}>Notes / Commentaires (optionnel)</div>
            <textarea className="ta" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Ajouter des notes d'appel..." />
          </div>

          {/* Barrage APPT — message dur (Karim 2026-10-16) : APPT sans RDV réel
              est impossible. Le serveur refuse aussi (409) — ce message couvre
              le clic navigateur ET le refus serveur. */}
          {apptBlocked && (
            <div style={{
              padding: '10px 12px', marginBottom: 12, borderRadius: 10, fontSize: 12,
              fontWeight: 700, lineHeight: 1.55, color: 'var(--red)',
              background: 'rgba(239,68,68,0.12)', border: '2px solid var(--red)',
            }}>
              🚫 {apptBlocked}
            </div>
          )}

          {/* Disposition grid — ONE CLICK = SUBMITTED (except RAPPEL which needs
              the date/time). CNC/AMD are engine-only codes, never agent-visible. */}
          <div style={{ fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text2)', marginBottom:8 }}>
            Cliquez le résultat de l'appel
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginBottom:6 }}>
            {visibleDispositions.map(d => (
              <button key={d.id} className={`dispo-btn ${selected?.id === d.id ? 'selected' : ''}`}
                disabled={submitting}
                onClick={() => handleDispositionClick(d)}
                title={d.is_callback ? 'Choisir la date et l’heure du rappel' : 'Un clic = enregistré, prochain appel arrive'}>
                <div style={{ width:12, height:12, borderRadius:'50%', background:d.color, flexShrink:0 }} />
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'var(--text)' }}>{d.label}</div>
                  <div style={{ display:'flex', gap:4, marginTop:2 }}>
                    {d.is_sale        && <span style={{ fontSize:9, padding:'1px 5px', borderRadius:100, background:'rgba(16,185,129,0.15)', color:'var(--green)', fontWeight:800 }}>VENTE</span>}
                    {d.is_dnc         && <span style={{ fontSize:9, padding:'1px 5px', borderRadius:100, background:'rgba(239,68,68,0.15)', color:'var(--red)', fontWeight:800 }}>DNC</span>}
                    {d.is_callback    && <span style={{ fontSize:9, padding:'1px 5px', borderRadius:100, background:'rgba(245,158,11,0.15)', color:'var(--orange)', fontWeight:800 }}>RAPPEL</span>}
                    {d.is_appointment && <span style={{ fontSize:9, padding:'1px 5px', borderRadius:100, background:'rgba(124,58,237,0.15)', color:'var(--purple)', fontWeight:800 }}>RDV</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div style={{ fontSize:10, color:'var(--text2)', textAlign:'center', marginBottom:14 }}>
            Un clic sur le résultat = enregistré · prochain appel automatique
          </div>

          {/* ── Calendar picker for callback ── */}
          {selected?.is_callback && (
            <div style={{ marginBottom:16, padding:'14px 16px', background:'rgba(124,58,237,0.04)', borderRadius:14, border:'2px solid rgba(124,58,237,0.2)' }}>
              <div style={{ fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--purple)', marginBottom:12 }}>
                📅 Date et heure du rappel {tz === 'America/Toronto' ? '(heure du Québec)' : `(fuseau ${tz})`}
              </div>
              <CalendarPicker value={callbackAt} onChange={setCallbackAt} tz={tz} />
            </div>
          )}

          {/* Pause after dispo — kept (Karim: agents must be able to pause),
              but out of the main flow: one small line under the grid. */}
          {selected && (
            <div style={{ marginBottom:12, fontSize:11 }}>
              <label style={{ display:'inline-flex', alignItems:'center', gap:7, cursor:'pointer', color:'var(--text2)' }}>
                <input type="checkbox" checked={pauseAfter} onChange={e => setPauseAfter(e.target.checked)} style={{ width:14, height:14, accentColor:'var(--purple)', cursor:'pointer' }} />
                ⏸ Pause après cette disposition
              </label>
              {pauseAfter && pauseCodes.length > 0 && (
                <select className="sel" style={{ marginLeft:8, width:'auto' }} value={pauseCodeId} onChange={e => setPauseCodeId(e.target.value)}>
                  {pauseCodes.filter(p => p.active).map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              )}
            </div>
          )}

          {/* Actions — the big submit only appears for RAPPEL (one-click
              dispositions already submitted above). Pause kept reachable. */}
          {selected?.is_callback && (
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-red btn-lg" style={{ flex:1 }}
                onClick={() => handleSubmit()}
                disabled={submitting || (selected?.is_callback && !callbackAt)}>
                {submitting ? 'Enregistrement...' : `SOUMETTRE : ${selected.label}`}
              </button>
            </div>
          )}

          <div style={{ display:'flex', gap:8, marginTop:10 }}>
            {/* Pause Now — kept per Karim: always available, minimal footprint */}
            <div style={{ position:'relative' }}>
              <button className="btn btn-lg" style={{ background:'linear-gradient(135deg,rgba(245,158,11,0.15),rgba(249,115,22,0.1))', border:'2px solid rgba(245,158,11,0.35)', color:'var(--orange)', whiteSpace:'nowrap' }}
                onClick={() => setShowPauseNow(!showPauseNow)}>
                ⏸ PAUSE MAINTENANT
              </button>
              {showPauseNow && (
                <div style={{ position:'absolute', bottom:'110%', right:0, background:'#fff', border:'2px solid var(--border)', borderRadius:14, padding:14, minWidth:210, boxShadow:'0 16px 40px rgba(124,58,237,0.2)', zIndex:10 }}>
                  <div className="lbl" style={{ marginBottom:7 }}>Raison de la pause</div>
                  <select className="sel" style={{ marginBottom:10 }} value={pauseNowCode} onChange={e => setPauseNowCode(e.target.value)}>
                    {pauseCodes.filter(p => p.active).map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
                  <button className="btn btn-orange" style={{ width:'100%' }} onClick={handlePauseNow}>
                    Raccrocher &amp; Pause
                  </button>
                </div>
              )}
            </div>

            <button className="btn btn-ghost btn-lg" onClick={onClose}>Annuler</button>
          </div>
        </div>
      </div>
    </div>
  );
}